/**
 * ===================================================================
 * EMAIL VALIDATOR & ANTI-SPAM
 * ===================================================================
 * 
 * Bảo vệ chống spam email và disposable email domains
 * 
 * Features:
 * - Email format validation
 * - Disposable/temporary email detection
 * - Rate limiting per IP/device
 * - Domain blacklist
 * 
 * ===================================================================
 */

/**
 * Danh sách các disposable/temporary email domains phổ biến
 * Cập nhật: Oct 2024
 */
const DISPOSABLE_EMAIL_DOMAINS = [
  // Popular temporary email services
  'grr.la',
  'guerrillamail.com',
  'guerrillamail.net',
  'guerrillamail.org',
  'guerrillamailblock.com',
  'mailinator.com',
  'mailinator.net',
  'mailinator2.com',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'tempmail.com',
  'temp-mail.org',
  'temp-mail.io',
  'tempinbox.com',
  'tempinbox.net',
  'throwaway.email',
  'throwawaymail.com',
  'trashmail.com',
  'trashmail.net',
  'getnada.com',
  'maildrop.cc',
  'mintemail.com',
  '10minutemail.com',
  '10minutemail.net',
  'emailondeck.com',
  'fakeinbox.com',
  'fakeinbox.net',
  'fakemail.net',
  'dispostable.com',
  'getairmail.com',
  'mvrht.com',
  'sharklasers.com',
  'spam4.me',
  'spamavert.com',
  'mohmal.com',
  'crazymailing.com',
  'trbvm.com',
  'mt2009.com',
  'mt2014.com',
  'mt2015.com',
  'mailcatch.com',
  'mailnesia.com',
  'anonymbox.com',
  'binkmail.com',
  'spamgourmet.com',
  'mytrashmail.com',
  'mailexpire.com',
  'mailforspam.com',
  'spambox.us',
  'spamfree24.org',
  'temporaryemail.net',
  'jetable.org',
  'meltmail.com',
  'spambog.com',
  'mailmoat.com',
  'armyspy.com',
  'cuvox.de',
  'dayrep.com',
  'einrot.com',
  'fleckens.hu',
  'gustr.com',
  'jourrapide.com',
  'rhyta.com',
  'superrito.com',
  'teleworm.us',
  'poczta.onet.pl',
  'interia.pl',
  'wp.pl',
  'o2.pl',
  // Thêm các domain Việt Nam hay bị spam
  'chothue.com',
  // Add more as needed
]

/**
 * Danh sách các domain không an toàn/đáng ngờ
 */
const SUSPICIOUS_DOMAINS = [
  'test.com',
  'test.net',
  'example.com',
  'example.net',
  'localhost.com',
  'mail.com', // Quá generic
]

/**
 * Regex pattern cho email hợp lệ (RFC 5322 simplified)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

/**
 * Validate email format
 */
export function isValidEmailFormat(email) {
  if (!email || typeof email !== 'string') {
    return false
  }

  // Trim và lowercase
  email = email.trim().toLowerCase()

  // Check basic format
  if (!EMAIL_REGEX.test(email)) {
    return false
  }

  // Check length
  if (email.length < 5 || email.length > 254) {
    return false
  }

  // Check cho các pattern spam
  const spamPatterns = [
    /^\d+@/, // Bắt đầu bằng chỉ toán
    /^test\d*@/, // test1@, test2@, etc.
    /^admin\d*@/, // admin1@, admin2@
    /^user\d*@/, // user1@, user2@
    /^fake/, // fake@...
    /^spam/, // spam@...
    /^trash/, // trash@...
    /^temp/, // temp@...
    /^\w{1}@/, // 1 ký tự trước @
  ]

  for (const pattern of spamPatterns) {
    if (pattern.test(email)) {
      return false
    }
  }

  return true
}

/**
 * Check if email domain is disposable/temporary
 */
export function isDisposableEmail(email) {
  if (!email || typeof email !== 'string') {
    return true // Reject invalid
  }

  const domain = email.trim().toLowerCase().split('@')[1]
  
  if (!domain) {
    return true
  }

  // Check exact match
  if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
    return true
  }

  // Check for subdomain matches (e.g., abc.mailinator.com)
  for (const disposableDomain of DISPOSABLE_EMAIL_DOMAINS) {
    if (domain.endsWith('.' + disposableDomain) || domain === disposableDomain) {
      return true
    }
  }

  return false
}

/**
 * Check if email domain is suspicious
 */
export function isSuspiciousEmail(email) {
  if (!email || typeof email !== 'string') {
    return true
  }

  const domain = email.trim().toLowerCase().split('@')[1]
  
  if (!domain) {
    return true
  }

  return SUSPICIOUS_DOMAINS.includes(domain)
}

/**
 * Rate limiting - Check if IP/device can register
 * Sử dụng localStorage để track
 */
const RATE_LIMIT_KEY = 'email_registration_limit'
const RATE_LIMIT_MINUTES = 10

export function canRegisterEmail() {
  try {
    const lastRegistration = localStorage.getItem(RATE_LIMIT_KEY)
    
    if (!lastRegistration) {
      return { allowed: true }
    }

    const lastTime = new Date(lastRegistration)
    const now = new Date()
    const diffMinutes = (now - lastTime) / 1000 / 60

    if (diffMinutes < RATE_LIMIT_MINUTES) {
      const remainingMinutes = Math.ceil(RATE_LIMIT_MINUTES - diffMinutes)
      return {
        allowed: false,
        remainingMinutes,
        message: `Vui lòng đợi ${remainingMinutes} phút trước khi đăng ký lại.`
      }
    }

    return { allowed: true }
  } catch (error) {
    // Nếu localStorage không khả dụng, cho phép đăng ký
    console.warn('localStorage not available:', error)
    return { allowed: true }
  }
}

/**
 * Mark that an email registration happened
 */
export function markEmailRegistered() {
  try {
    localStorage.setItem(RATE_LIMIT_KEY, new Date().toISOString())
  } catch (error) {
    console.warn('Cannot set localStorage:', error)
  }
}

/**
 * Reset rate limit (for testing)
 */
export function resetRateLimit() {
  try {
    localStorage.removeItem(RATE_LIMIT_KEY)
  } catch (error) {
    console.warn('Cannot remove from localStorage:', error)
  }
}

/**
 * Validate email toàn diện
 * @returns { valid: boolean, error: string }
 */
export function validateEmail(email, checkRateLimit = true) {
  // 1. Check rate limit
  if (checkRateLimit) {
    const rateLimitCheck = canRegisterEmail()
    if (!rateLimitCheck.allowed) {
      return {
        valid: false,
        error: rateLimitCheck.message
      }
    }
  }

  // 2. Check format
  if (!isValidEmailFormat(email)) {
    return {
      valid: false,
      error: 'Email không đúng định dạng. Vui lòng nhập email hợp lệ.'
    }
  }

  // 3. Check disposable
  if (isDisposableEmail(email)) {
    return {
      valid: false,
      error: 'Email tạm thời không được chấp nhận. Vui lòng sử dụng email thật.'
    }
  }

  // 4. Check suspicious
  if (isSuspiciousEmail(email)) {
    return {
      valid: false,
      error: 'Email này không được chấp nhận. Vui lòng sử dụng email khác.'
    }
  }

  return {
    valid: true,
    error: null
  }
}

/**
 * Get recommended email providers (for UI hints)
 */
export const RECOMMENDED_EMAIL_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
  'protonmail.com',
  // Vietnamese providers
  'fpt.vn',
  'vn.edu.vn',
  'vnpt.vn',
]

/**
 * Check if email is from a recommended provider
 */
export function isRecommendedProvider(email) {
  if (!email || typeof email !== 'string') {
    return false
  }

  const domain = email.trim().toLowerCase().split('@')[1]
  return RECOMMENDED_EMAIL_PROVIDERS.includes(domain)
}

/**
 * Get email validation stats (for admin)
 */
export function getValidationStats(email) {
  return {
    format: isValidEmailFormat(email),
    disposable: isDisposableEmail(email),
    suspicious: isSuspiciousEmail(email),
    recommended: isRecommendedProvider(email),
    domain: email.split('@')[1]?.toLowerCase() || 'unknown'
  }
}

export default {
  validateEmail,
  isValidEmailFormat,
  isDisposableEmail,
  isSuspiciousEmail,
  canRegisterEmail,
  markEmailRegistered,
  resetRateLimit,
  isRecommendedProvider,
  getValidationStats,
  RECOMMENDED_EMAIL_PROVIDERS
}

